import { useEffect, useRef } from 'react';
import type { FC, RefObject } from 'react';
import { Room, RoomEvent, Track, VideoQuality } from 'livekit-client';
import type { RemoteTrack, RemoteTrackPublication } from 'livekit-client';
import { useClerkToken } from '@/hooks/useClerkToken';
import { PLAY_CLAW_JWT_TEMPLATE } from '@/lib/auth';
import { getTraceId, logError, logInfo, logWarn } from '@/lib/logger';
import { isVideoDebugEnabledFromLocation } from '@/lib/videoDebug';
import { isMobileSafariBrowser } from '@/lib/videoFraming';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/$/, '');
const DEFAULT_TRACK = 'robot_overhead';
const TRACK_ORDER = ['robot_overhead', 'robot_perspective'];
const LIVEKIT_DEBUG = import.meta.env.VITE_LIVEKIT_DEBUG === 'true';
const LIVEKIT_VIDEO_DEBUG = isVideoDebugEnabledFromLocation();
const LIVEKIT_MOBILE_SAFARI = isMobileSafariBrowser();
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 10000;
const TOKEN_TIMEOUT_MS = 8000;
const CLERK_TOKEN_TIMEOUT_MS = 8000;
const LIVEKIT_CONNECT_PENDING_LOG_MS = 15000;
const STALE_VIDEO_CHECK_INTERVAL_MS = 1000;
const STALE_VIDEO_TIMEOUT_MS = 6000;
const VIDEO_PROGRESS_EPSILON_SECONDS = 0.01;

type LiveKitOperatorProviderProps = {
  roomName: string;
  videoRef: RefObject<HTMLVideoElement>;
  onStatusChange: (status: 'idle' | 'connecting' | 'connected' | 'failed') => void;
  activeTrackName?: string;
  onTracksChange?: (tracks: string[]) => void;
  onActiveTrackChange?: (trackName: string) => void;
  onRoom?: (room: Room | null) => void;
};

type LiveKitTokenResponse = {
  url: string;
  token: string;
  room_id: string;
  identity: string;
};

const fetchToken = async (roomName: string, authToken?: string | null): Promise<LiveKitTokenResponse> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), TOKEN_TIMEOUT_MS);
  const response = await fetch(`${API_BASE}/api/livekit/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Trace-Id': getTraceId(),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({ robot_id: roomName, room_id: roomName, role: 'operator' }),
    signal: controller.signal,
  });
  window.clearTimeout(timeoutId);

  if (LIVEKIT_DEBUG) {
    logInfo('livekit.token.response', 'token response', { status: response.status });
  }

  let rawText = '';
  try {
    rawText = await response.text();
  } catch (error) {
    if (LIVEKIT_DEBUG) {
      logWarn('livekit.token.read_failed', 'token response read failed', { error: String(error) });
    }
  }

  if (!response.ok) {
    throw new Error(`LiveKit token request failed: ${response.status} ${rawText}`.trim());
  }

  if (LIVEKIT_DEBUG) {
    logInfo('livekit.token.body_length', 'token body length', { length: rawText.length });
  }

  try {
    return JSON.parse(rawText) as LiveKitTokenResponse;
  } catch (error) {
    if (LIVEKIT_DEBUG) {
      logError('livekit.token.parse_failed', 'token body parse failed', { error: String(error) });
    }
    throw error;
  }
};

const sortTracks = (tracks: string[]) =>
  [...new Set(tracks)].sort((a, b) => {
    const aIndex = TRACK_ORDER.indexOf(a);
    const bIndex = TRACK_ORDER.indexOf(b);
    if (aIndex === -1 && bIndex === -1) {
      return a.localeCompare(b);
    }
    if (aIndex === -1) {
      return 1;
    }
    if (bIndex === -1) {
      return -1;
    }
    return aIndex - bIndex;
  });

const getTrackName = (publication: RemoteTrackPublication) =>
  publication.trackName || (publication as { name?: string }).name || '';

const applyVideoSubscriptionPreference = (
  publication: RemoteTrackPublication,
  isActive: boolean
) => {
  publication.setSubscribed(isActive);
  if (isActive) {
    publication.setVideoQuality(VideoQuality.HIGH);
  }
};

const collectVideoTracks = (room: Room) => {
  const tracks: string[] = [];
  room.remoteParticipants.forEach((participant) => {
    participant.trackPublications.forEach((publication) => {
      if (publication.kind !== Track.Kind.Video) {
        return;
      }
      const name = getTrackName(publication);
      if (name) {
        tracks.push(name);
      }
    });
  });
  return sortTracks(tracks);
};

const enforceVideoElementLayout = (video: HTMLVideoElement) => {
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', 'true');
  video.setAttribute('webkit-playsinline', 'true');
  if (!video.style.position) {
    video.style.position = 'absolute';
  }
  if (!video.style.left) {
    video.style.left = '0px';
  }
  if (!video.style.top) {
    video.style.top = '0px';
  }
  if (!video.style.width) {
    video.style.width = '100%';
  }
  if (!video.style.height) {
    video.style.height = '100%';
  }
  if (!video.style.objectFit) {
    video.style.objectFit = 'cover';
  }
};

const playAttachedVideo = (video: HTMLVideoElement, context: string) => {
  const playPromise = video.play();
  if (!playPromise) {
    return;
  }
  playPromise.catch((error) => {
    logWarn('livekit.video.play_failed', 'video play failed', {
      context,
      error: String(error),
      ready_state: video.readyState,
      network_state: video.networkState,
      paused: video.paused,
      muted: video.muted,
      plays_inline: video.playsInline,
      video_width: video.videoWidth,
      video_height: video.videoHeight,
    });
  });
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(`${label}.timeout`));
    }, timeoutMs);
    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
};

const LiveKitOperatorProvider: FC<LiveKitOperatorProviderProps> = ({
  roomName,
  videoRef,
  onStatusChange,
  activeTrackName = DEFAULT_TRACK,
  onTracksChange,
  onActiveTrackChange,
  onRoom,
}) => {
  const activeTrackRef = useRef<RemoteTrack | null>(null);
  const activeTrackNameRef = useRef(activeTrackName || DEFAULT_TRACK);
  const roomRef = useRef<Room | null>(null);
  const trackMapRef = useRef<Map<string, RemoteTrack>>(new Map());
  const tracksRef = useRef<string[]>([]);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const videoWatchdogIntervalRef = useRef<number | null>(null);
  const lastVideoTimeRef = useRef(0);
  const lastVideoProgressAtRef = useRef(0);
  const staleDisconnectInFlightRef = useRef(false);
  const { getToken, isLoaded, isSignedIn } = useClerkToken(PLAY_CLAW_JWT_TEMPLATE);

  useEffect(() => {
    activeTrackNameRef.current = activeTrackName || DEFAULT_TRACK;
    const room = roomRef.current;
    if (!room) {
      return;
    }

    const tracks = tracksRef.current;
    if (tracks.length && !tracks.includes(activeTrackNameRef.current)) {
      const fallback = tracks[0];
      if (fallback && fallback !== activeTrackNameRef.current) {
        activeTrackNameRef.current = fallback;
        onActiveTrackChange?.(fallback);
      }
    }

    const desired = activeTrackNameRef.current;
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.kind !== Track.Kind.Video) {
          return;
        }
        const name = getTrackName(publication);
        if (!name) {
          return;
        }
        applyVideoSubscriptionPreference(publication, name === desired);
      });
    });

    const nextTrack = trackMapRef.current.get(activeTrackNameRef.current);
    if (nextTrack && videoRef.current) {
      if (activeTrackRef.current && activeTrackRef.current !== nextTrack) {
        activeTrackRef.current.detach(videoRef.current);
      }
      activeTrackRef.current = nextTrack;
      nextTrack.attach(videoRef.current);
      enforceVideoElementLayout(videoRef.current);
      playAttachedVideo(videoRef.current, 'active_track_switch');
      lastVideoTimeRef.current = videoRef.current.currentTime;
      lastVideoProgressAtRef.current = performance.now();
      staleDisconnectInFlightRef.current = false;
    }
  }, [activeTrackName, onActiveTrackChange, videoRef]);

  useEffect(() => {
    if (!roomName) {
      return;
    }

    let canceled = false;
    const room = LIVEKIT_MOBILE_SAFARI
      ? new Room({
          publishDefaults: { videoCodec: 'h264' },
          singlePeerConnection: false,
        })
      : new Room({
          publishDefaults: { videoCodec: 'h264' },
        });
    const connectOptions = LIVEKIT_MOBILE_SAFARI
      ? {
          autoSubscribe: false,
          peerConnectionTimeout: 30_000,
          rtcConfig: {
            iceTransportPolicy: 'relay' as RTCIceTransportPolicy,
          },
        }
      : {
          autoSubscribe: false,
        };
    const trackMap = trackMapRef.current;
    roomRef.current = room;
    reconnectAttemptRef.current = 0;
    if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
      logInfo('livekit.connect.options', 'connect options', {
        room: roomName,
        mobile_safari_mode: LIVEKIT_MOBILE_SAFARI,
        single_peer_connection: LIVEKIT_MOBILE_SAFARI ? false : true,
        peer_connection_timeout_ms: connectOptions.peerConnectionTimeout ?? 15_000,
        ice_transport_policy: connectOptions.rtcConfig?.iceTransportPolicy ?? 'all',
      });
    }

    const clearVideoWatchdog = () => {
      if (videoWatchdogIntervalRef.current) {
        clearInterval(videoWatchdogIntervalRef.current);
        videoWatchdogIntervalRef.current = null;
      }
    };

    const markVideoProgress = () => {
      const video = videoRef.current;
      lastVideoTimeRef.current = video?.currentTime ?? 0;
      lastVideoProgressAtRef.current = performance.now();
      staleDisconnectInFlightRef.current = false;
    };

    const clearPlaybackState = () => {
      lastVideoTimeRef.current = 0;
      lastVideoProgressAtRef.current = 0;
      staleDisconnectInFlightRef.current = false;
    };

    const detachActiveTrack = () => {
      if (activeTrackRef.current && videoRef.current) {
        activeTrackRef.current.detach(videoRef.current);
      }
      activeTrackRef.current = null;
      clearPlaybackState();
    };

    const clearReconnectTimeout = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (canceled || reconnectTimeoutRef.current) {
        return;
      }
      const delay = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttemptRef.current,
        RECONNECT_MAX_DELAY_MS
      );
      reconnectAttemptRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        void connect();
      }, delay);
      if (LIVEKIT_DEBUG) {
        logWarn('livekit.reconnect.scheduled', 'reconnect scheduled', { delay });
      }
    };

    const triggerStaleRecovery = (stalledForMs: number) => {
      if (canceled || staleDisconnectInFlightRef.current) {
        return;
      }
      staleDisconnectInFlightRef.current = true;
      logWarn('livekit.video.stale', 'video appears stale; forcing reconnect', {
        room: roomName,
        duration_ms: Math.round(stalledForMs),
      });
      detachActiveTrack();
      trackMap.clear();
      tracksRef.current = [];
      onTracksChange?.([]);
      onStatusChange('failed');
      onRoom?.(null);
      scheduleReconnect();
      void room.disconnect();
    };

    const refreshTracks = () => {
      const publicationSummaries: Array<{
        participant: string;
        trackName: string;
        sid: string;
        subscribed: boolean;
      }> = [];
      const tracks = collectVideoTracks(room);
      room.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          if (publication.kind !== Track.Kind.Video) {
            return;
          }
          publicationSummaries.push({
            participant: participant.identity,
            trackName: getTrackName(publication) || '(unnamed)',
            sid: publication.trackSid,
            subscribed: publication.isSubscribed,
          });
        });
      });
      if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
        logInfo('livekit.tracks.refresh', 'refresh tracks', {
          room: roomName,
          track_count: tracks.length,
          tracks,
          publication_count: publicationSummaries.length,
          publications: publicationSummaries,
        });
      }
      tracksRef.current = tracks;
      onTracksChange?.(tracks);

      if (!tracks.length) {
        return;
      }

      const desired = activeTrackNameRef.current || DEFAULT_TRACK;
      if (!tracks.includes(desired)) {
        const fallback = tracks[0];
        if (fallback && fallback !== desired) {
          activeTrackNameRef.current = fallback;
          onActiveTrackChange?.(fallback);
        }
      }

      const activeName = activeTrackNameRef.current || DEFAULT_TRACK;
      room.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          if (publication.kind !== Track.Kind.Video) {
            return;
          }
          const name = getTrackName(publication);
          if (!name) {
            return;
          }
          applyVideoSubscriptionPreference(publication, name === activeName);
        });
      });
    };

    const handleTrackSubscribed = (track: RemoteTrack, publication: RemoteTrackPublication) => {
      if (track.kind !== Track.Kind.Video) {
        return;
      }

      const trackName = getTrackName(publication);
      if (!trackName) {
        return;
      }

      applyVideoSubscriptionPreference(
        publication,
        trackName === (activeTrackNameRef.current || DEFAULT_TRACK)
      );

      trackMap.set(trackName, track);

      if (!videoRef.current) {
        return;
      }

      if (trackName === (activeTrackNameRef.current || DEFAULT_TRACK)) {
        if (activeTrackRef.current && activeTrackRef.current !== track) {
          detachActiveTrack();
        }

        activeTrackRef.current = track;
        track.attach(videoRef.current);
        enforceVideoElementLayout(videoRef.current);
        playAttachedVideo(videoRef.current, 'track_subscribed');
        markVideoProgress();
      }

      refreshTracks();
    };

    const handleTrackUnsubscribed = (track: RemoteTrack) => {
      if (activeTrackRef.current === track) {
        detachActiveTrack();
      }
      for (const [name, mappedTrack] of trackMap.entries()) {
        if (mappedTrack === track) {
          trackMap.delete(name);
          break;
        }
      }
      refreshTracks();
    };

    const handleDisconnected = () => {
      detachActiveTrack();
      trackMap.clear();
      tracksRef.current = [];
      onTracksChange?.([]);
      onStatusChange('failed');
      onRoom?.(null);
      scheduleReconnect();
    };

    const handleConnectionStateChanged = (state: string) => {
      if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
        logInfo('livekit.connection.state', 'connection state', { state });
      }
    };
    const handleSignalConnected = () => {
      if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
        logInfo('livekit.signal.connected', 'signal connected', { room: roomName });
      }
    };

    const handleReconnecting = () => {
      onStatusChange('connecting');
      if (LIVEKIT_DEBUG) {
        logWarn('livekit.reconnecting', 'reconnecting');
      }
    };

    const handleReconnected = () => {
      onStatusChange('connected');
      reconnectAttemptRef.current = 0;
      clearReconnectTimeout();
      if (LIVEKIT_DEBUG) {
        logInfo('livekit.reconnected', 'reconnected');
      }
    };
    const handleTrackSubscriptionFailed = (trackSid: string, participant: unknown, error: unknown) => {
      const participantIdentity =
        participant && typeof participant === 'object' && 'identity' in participant
          ? String((participant as { identity?: string }).identity || '')
          : '';
      logWarn('livekit.track.subscription_failed', 'track subscription failed', {
        room: roomName,
        track_sid: trackSid,
        participant_identity: participantIdentity,
        error: String(error),
      });
    };

    const startVideoWatchdog = () => {
      clearVideoWatchdog();
      markVideoProgress();
      videoWatchdogIntervalRef.current = window.setInterval(() => {
        if (canceled || !activeTrackRef.current) {
          return;
        }
        const video = videoRef.current;
        if (!video) {
          return;
        }
        const now = performance.now();
        const currentTime = video.currentTime;
        if (currentTime > lastVideoTimeRef.current + VIDEO_PROGRESS_EPSILON_SECONDS) {
          lastVideoTimeRef.current = currentTime;
          lastVideoProgressAtRef.current = now;
          staleDisconnectInFlightRef.current = false;
          return;
        }
        if (!lastVideoProgressAtRef.current) {
          lastVideoTimeRef.current = currentTime;
          lastVideoProgressAtRef.current = now;
          return;
        }
        if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || video.paused || video.ended) {
          const stalledForMs = now - lastVideoProgressAtRef.current;
          if (stalledForMs >= STALE_VIDEO_TIMEOUT_MS) {
            triggerStaleRecovery(stalledForMs);
          }
          return;
        }
        const stalledForMs = now - lastVideoProgressAtRef.current;
        if (stalledForMs >= STALE_VIDEO_TIMEOUT_MS) {
          triggerStaleRecovery(stalledForMs);
        }
      }, STALE_VIDEO_CHECK_INTERVAL_MS);
    };

    room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
    room.on(RoomEvent.TrackPublished, refreshTracks);
    room.on(RoomEvent.TrackUnpublished, refreshTracks);
    room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
    room.on(RoomEvent.SignalConnected, handleSignalConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.Reconnecting, handleReconnecting);
    room.on(RoomEvent.Reconnected, handleReconnected);
    room.on(RoomEvent.TrackSubscriptionFailed, handleTrackSubscriptionFailed);
    room.on(RoomEvent.ParticipantConnected, refreshTracks);
    room.on(RoomEvent.ParticipantDisconnected, refreshTracks);
    startVideoWatchdog();

    let connectInFlight = false;
    const connect = async () => {
      if (connectInFlight || canceled) {
        return;
      }
      connectInFlight = true;
      let connectPhase:
        | 'init'
        | 'auth_wait'
        | 'clerk_token'
        | 'livekit_token'
        | 'room_connect'
        | 'post_connect' = 'init';
      try {
        onStatusChange('connecting');
        if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
          logInfo('livekit.connect.begin', 'connect begin', {
            room: roomName,
            isLoaded,
            isSignedIn,
          });
        }
        if (LIVEKIT_DEBUG) {
          logInfo('livekit.token.fetching', 'fetching token', { room: roomName });
        }
        if (!isLoaded || !isSignedIn) {
          if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
            logWarn('livekit.auth.not_ready', 'auth not ready', { isLoaded, isSignedIn });
          }
          scheduleReconnect();
          return;
        }
        connectPhase = 'clerk_token';
        if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
          logInfo('livekit.auth.token.requested', 'requesting auth token', { room: roomName });
        }
        const authStart = performance.now();
        const authToken = await withTimeout(getToken(), CLERK_TOKEN_TIMEOUT_MS, 'livekit.auth.token');
        if (LIVEKIT_DEBUG) {
          logInfo('livekit.auth.token_resolved', 'auth token resolved', {
            hasToken: Boolean(authToken),
            ms: Math.round(performance.now() - authStart),
          });
        }
        if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
          logInfo('livekit.auth.token.ready', 'auth token ready', {
            hasToken: Boolean(authToken),
            ms: Math.round(performance.now() - authStart),
          });
        }
        if (!authToken) {
          throw new Error('Missing auth token');
        }
        connectPhase = 'livekit_token';
        if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
          logInfo('livekit.token.requested', 'requesting livekit token', { room: roomName });
        }
        const tokenStart = performance.now();
        const { url, token } = await fetchToken(roomName, authToken);
        if (canceled) {
          return;
        }
        if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
          logInfo('livekit.token.received', 'token received', { ms: Math.round(performance.now() - tokenStart) });
        }
        if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
          let urlHost = '';
          let urlProtocol = '';
          try {
            const parsedUrl = new URL(url);
            urlHost = parsedUrl.host;
            urlProtocol = parsedUrl.protocol;
          } catch {
            // keep empty when URL cannot be parsed
          }
          logInfo('livekit.connecting', 'connecting', {
            room: roomName,
            url_host: urlHost,
            url_protocol: urlProtocol,
          });
        }
        connectPhase = 'room_connect';
        if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
          logInfo('livekit.room.connect.call', 'calling room.connect', { room: roomName });
        }
        const connectStart = performance.now();
        let pendingLogTimer: number | null = null;
        if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
          pendingLogTimer = window.setTimeout(() => {
            const roomState = String((room as unknown as { state?: string }).state ?? 'unknown');
            logWarn('livekit.room.connect.pending', 'room.connect still pending', {
              room: roomName,
              pending_ms: LIVEKIT_CONNECT_PENDING_LOG_MS,
              room_state: roomState,
            });
          }, LIVEKIT_CONNECT_PENDING_LOG_MS);
        }
        try {
          await room.connect(url, token, connectOptions);
        } finally {
          if (pendingLogTimer !== null) {
            window.clearTimeout(pendingLogTimer);
          }
        }
        if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
          logInfo('livekit.room.connect.resolved', 'room.connect resolved', {
            room: roomName,
            ms: Math.round(performance.now() - connectStart),
          });
        }
        if (!canceled) {
          connectPhase = 'post_connect';
          refreshTracks();
          onStatusChange('connected');
          onRoom?.(room);
          reconnectAttemptRef.current = 0;
          clearReconnectTimeout();
          if (LIVEKIT_DEBUG || LIVEKIT_VIDEO_DEBUG) {
            logInfo('livekit.connected', 'connected', {
              room: roomName,
              remote_participants: room.remoteParticipants.size,
            });
          }
        }
      } catch (error) {
        if (!canceled) {
          logError('livekit.connect.failed', 'connect failed', { phase: connectPhase, error: String(error) });
          onStatusChange('failed');
          onRoom?.(null);
          scheduleReconnect();
        }
      } finally {
        connectInFlight = false;
      }
    };

    connect();

    return () => {
      canceled = true;
      detachActiveTrack();
      trackMap.clear();
      tracksRef.current = [];
      onTracksChange?.([]);
      onRoom?.(null);
      clearVideoWatchdog();
      room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      room.off(RoomEvent.TrackPublished, refreshTracks);
      room.off(RoomEvent.TrackUnpublished, refreshTracks);
      room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
      room.off(RoomEvent.SignalConnected, handleSignalConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.Reconnecting, handleReconnecting);
      room.off(RoomEvent.Reconnected, handleReconnected);
      room.off(RoomEvent.TrackSubscriptionFailed, handleTrackSubscriptionFailed);
      room.off(RoomEvent.ParticipantConnected, refreshTracks);
      room.off(RoomEvent.ParticipantDisconnected, refreshTracks);
      clearReconnectTimeout();
      room.disconnect();
      onStatusChange('idle');
    };
  }, [
    roomName,
    videoRef,
    onStatusChange,
    onTracksChange,
    onActiveTrackChange,
    onRoom,
    getToken,
    isLoaded,
    isSignedIn,
  ]);

  return null;
};

export default LiveKitOperatorProvider;
